import { useFormik } from 'formik';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ObjectSchema } from 'yup';

import { SendDetailsForm } from '../../../components';
import { routes } from '../../../constants/routing';
import {
  DELAYED_SHIPPING_URL,
  getSendDetailsFormValidationSchema,
  INITIAL_DETAILS_FORM_STATE as initialFormValues,
  RECEIVER_EMAIL,
  RECEIVER_FIRST_NAME,
  RECEIVER_LAST_NAME,
  RECEIVER_PHONE,
} from '../../../constants/shipping';
import { MESSAGE } from '../../../constants/templates';
import { fetchFulfillmentCentersRequest } from '../../../store/actions/fulfillmentCenters';
import { populateReportCampaignSummariesRequest } from '../../../store/actions/reports';
import { selectHasItemsWithEnabledShippingOptions } from '../../../store/selectors/fulfillmentCenters';
import { selectIsSingleDeliveryReportsFetching } from '../../../store/selectors/reports';
import { MaintenanceFormStateEnum } from '../../../types/forms';
import {
  EditSendSubmitResultEnum,
  EngagementSourceTypesEnum,
  IReportWithCampaignSummaries,
} from '../../../types/reports';
import { IReportsRouteParams } from '../../../types/routing';
import { IReceiverInfoDetailsFormValues, ISendDetailsFormValues } from '../../../types/shipping';
import { getRequiredFields } from '../../../utils/form';
import { isRealCampaignId, splitPickCampaignIds } from '../../../utils/marketplaceSend';
import { getEditableSendFields, isAwaitingAddress, isCustomReport, isReportViewed } from '../../../utils/reports';

export interface IEditEngagementDetailsSidebarProps {
  onCancel: () => void;
  onSubmit: (values: IReceiverInfoDetailsFormValues) => Promise<EditSendSubmitResultEnum>;
  report: IReportWithCampaignSummaries | null | undefined;
}

export interface IEditDetailsRef extends Partial<IReceiverInfoDetailsFormValues> {
  getValues: () => IReceiverInfoDetailsFormValues;
  submit: () => Promise<EditSendSubmitResultEnum>;
}

const SEND_DETAILS_FIELD_ORDER: string[] = [
  RECEIVER_FIRST_NAME,
  RECEIVER_LAST_NAME,
  RECEIVER_PHONE,
  RECEIVER_EMAIL,
  MESSAGE,
];

const SUBMIT_RESULTS: string[] = Object.values(EditSendSubmitResultEnum);

const isSubmitResult = (value: unknown): value is EditSendSubmitResultEnum =>
  typeof value === 'string' && SUBMIT_RESULTS.includes(value);

const EditEngagementDetailsContainer: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<IEditEngagementDetailsSidebarProps> & React.RefAttributes<IEditDetailsRef>
> = React.forwardRef(({ report, onCancel, onSubmit }, ref?: React.Ref<IEditDetailsRef>) => {
  const dispatch = useDispatch();
  const { reportId, type } = useParams<IReportsRouteParams>();

  const isShippingOptionEnabled = useSelector(selectHasItemsWithEnabledShippingOptions(report?.items));
  const isLoading = useSelector(selectIsSingleDeliveryReportsFetching);

  const initialValues = React.useMemo(
    () => ({
      ...initialFormValues,
      ...getEditableSendFields(report),
    }),
    [report],
  );

  const isAddressFixed = React.useMemo(() => report?.is_receiver_address_fixed, [report]);

  const isDelayedShipping = React.useMemo(() => !!report?.[DELAYED_SHIPPING_URL], [report]);

  // Phone is optional only while the send is awaiting an address (or has been viewed but not yet
  // addressed). Once the status moves on to any other value, phone becomes required again.
  const isPhoneOptional = React.useMemo(
    () => (report ? isAwaitingAddress(report) || isReportViewed(report) : false),
    [report],
  );

  const initialEmail = report?.[RECEIVER_EMAIL] ?? '';

  const validationSchema = React.useMemo(
    () => getSendDetailsFormValidationSchema({ isPhoneOptional, initialEmail }),
    [isPhoneOptional, initialEmail],
  );

  const formRef = React.useRef<HTMLFormElement>(null);

  const handleSubmit = React.useCallback(
    async (values: IReceiverInfoDetailsFormValues): Promise<EditSendSubmitResultEnum> => {
      if (!validationSchema.isValidSync(values)) {
        return EditSendSubmitResultEnum.Invalid;
      }

      return onSubmit(values);
    },
    [onSubmit, validationSchema],
  );

  const form = useFormik<IReceiverInfoDetailsFormValues>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const focusFirstInvalidField = React.useCallback((errors: Record<string, unknown>) => {
    const field = SEND_DETAILS_FIELD_ORDER.find(
      (name) => errors[name] && formRef.current?.querySelector(`[name="${name}"]`),
    );
    if (field) {
      formRef.current?.querySelector<HTMLElement>(`[name="${field}"]`)?.focus();
    }
  }, []);

  const submitWithResult = React.useCallback(async (): Promise<EditSendSubmitResultEnum> => {
    const errors = await form.validateForm();
    if (Object.keys(errors).length) {
      focusFirstInvalidField(errors);
      return EditSendSubmitResultEnum.Invalid;
    }

    const result: unknown = await form.submitForm();
    return isSubmitResult(result) ? result : EditSendSubmitResultEnum.Invalid;
  }, [form, focusFirstInvalidField]);

  React.useImperativeHandle(
    ref,
    () => ({
      getValues: () => form.values,
      submit: submitWithResult,
    }),
    [form.values, submitWithResult],
  );

  const requiredFields = React.useMemo(
    () => getRequiredFields(validationSchema as ObjectSchema<IReceiverInfoDetailsFormValues>),
    [validationSchema],
  );

  const isNoteHidden = React.useMemo(() => {
    if (report && isCustomReport(report)) {
      return !initialValues[MESSAGE];
    }

    if (report?.pick_campaigns) {
      return report.pick_campaigns.every((campaign) => campaign.disable_handwritten_note_step);
    }

    return true;
  }, [report, initialValues]);

  const isDirectSend = React.useMemo(
    () => report?.engagement_source_type === EngagementSourceTypesEnum.DirectSend,
    [report],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof ISendDetailsFormValues, value: string | number | undefined | string[] | boolean) => {
      form.setFieldValue(name, value, true);
    },
    [form.setFieldValue],
  );

  React.useEffect(() => {
    dispatch(fetchFulfillmentCentersRequest());
  }, [dispatch]);

  React.useEffect(() => {
    if (reportId) {
      const { campaignIds } = splitPickCampaignIds(report?.pick_campaign_ids);

      if (campaignIds.length && !report?.pick_campaigns?.length) {
        dispatch(populateReportCampaignSummariesRequest({ campaign_ids: campaignIds, reportId, type }));
      } else if (report && isRealCampaignId(report.pre_created_engagement_id) && !report.pick_campaigns?.length) {
        dispatch(
          populateReportCampaignSummariesRequest({ campaign_ids: [report.pre_created_engagement_id], reportId, type }),
        );
      }
    }
  }, [report?.pick_campaign_ids, report?.pre_created_engagement_id, report?.pick_campaigns, dispatch, reportId]);

  return (
    <SendDetailsForm
      root={routes.reports.root}
      requiredFields={requiredFields}
      form={form}
      onChange={handleFormFieldChange}
      mode={MaintenanceFormStateEnum.Editing}
      onCancel={onCancel}
      formRef={formRef}
      isEditingShipmentOptionsAllowed={isShippingOptionEnabled}
      isNoteHidden={isNoteHidden}
      isDirectSend={isDirectSend}
      isAddressFixed={isAddressFixed}
      isDelayedShipping={isDelayedShipping}
      isLoading={isLoading}
    />
  );
});

export default EditEngagementDetailsContainer;
